/**
 * Module Double Poutre - Service 3D
 * 
 * Reprend exactement la même logique que StructureBase3DService
 * Génération 3D pour une structure indépendante plus haute
 */

import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  ParametresDoublePoutre,
  MontantDoublePoutre,
  PoutreDoublePoutre,
  StructureDoublePoutre
} from '../types';
import { TypeMateriauGlobal, GeometrieForme } from '../../structure-base/types';
// import { callDoublePoutreAPI } from '../api/DoublePoutreAPI'; // Plus nécessaire - génération locale

/**
 * Service de génération 3D pour la double poutre
 * Reprend exactement la logique de StructureBase3DService
 */
export class DoublePoutre3DService {
  private groupeDoublePoutre: THREE.Group;
  private structureGeneree: StructureDoublePoutre | null = null;

  constructor() {
    this.groupeDoublePoutre = new THREE.Group();
    this.groupeDoublePoutre.name = 'double-poutre';
  }

  /**
   * Retourne le groupe contenant la structure de la double poutre
   */
  getGroupe(): THREE.Group {
    return this.groupeDoublePoutre;
  }

  /**
   * Récupère la structure générée avec les montants
   */
  getStructure(): StructureDoublePoutre | null {
    return this.structureGeneree;
  }

  /**
   * Génère la structure 3D complète de la double poutre
   * Génération locale sans appel API pour éviter la latence
   */
  async genererStructure(
    parametres: ParametresDoublePoutre,
    formeBase: GeometrieForme,
    hauteurStructurePrincipale: number
  ): Promise<THREE.Group> {
    try {
      // Nettoyage
      this.clearStructure();
      
      if (!parametres.active) {
        return this.groupeDoublePoutre;
      }

      // Génération locale de la structure
      const structure = this.genererStructureLocale(parametres, formeBase, hauteurStructurePrincipale);
      
      // Stocker la structure générée
      this.structureGeneree = structure;
      
      // Génération des poutres 3D
      this.genererPoutres3D(structure.poutres, parametres.materiau, parametres.epaisseur, parametres.couleur);

      // Génération des montants 3D
      this.genererMontants3D(structure.montants, parametres.materiau, parametres.epaisseur, parametres.couleur);

      return this.groupeDoublePoutre;
    } catch (error) {
      // Erreur génération structure
      return this.groupeDoublePoutre;
    }
  }

  /**
   * Génère la structure de la double poutre localement (sans API)
   * Reprend la logique du serveur pour éviter la latence
   */
  private genererStructureLocale(
    parametres: ParametresDoublePoutre,
    formeBase: GeometrieForme,
    hauteurStructurePrincipale: number
  ): StructureDoublePoutre {
    // Génération des montants
    const montants = this.genererMontantsLocaux(parametres.nombreMontants, formeBase, hauteurStructurePrincipale, parametres.hauteur);
    
    // Génération des poutres
    const poutres = this.genererPoutresLocales(formeBase, hauteurStructurePrincipale, parametres.hauteur, parametres.epaisseur);
    
    return {
      forme: formeBase,
      montants,
      poutres,
      parametres,
      nombreMontants: montants.length
    };
  }

  /**
   * Génère les montants de la double poutre localement
   */
  private genererMontantsLocaux(
    nombreMontants: number,
    formeBase: GeometrieForme,
    hauteurStructurePrincipale: number,
    hauteurDoublePoutre: number
  ): MontantDoublePoutre[] {
    const montants: MontantDoublePoutre[] = [];
    const points = formeBase.points;
    
    if (!points || points.length === 0) {
      return montants;
    }

    // Logique similaire au serveur pour distribuer les montants
    if (formeBase.type === 'cercle' || formeBase.type === 'ovale') {
      // Pour les formes circulaires/ovales
      for (let i = 0; i < nombreMontants; i++) {
        const angle = (i / nombreMontants) * Math.PI * 2;
        const point = points[Math.floor((i / nombreMontants) * (points.length - 1))];
        
        montants.push({
          id: `montant-double-poutre-${i + 1}`,
          position: {
            x: point.x,
            y: hauteurStructurePrincipale,
            z: point.z
          },
          positionBas: {
            x: point.x,
            y: hauteurStructurePrincipale,
            z: point.z
          },
          positionHaut: {
            x: point.x,
            y: hauteurStructurePrincipale + hauteurDoublePoutre,
            z: point.z
          },
          hauteur: hauteurDoublePoutre,
          section: { largeur: 0.1, profondeur: 0.1 }
        });
      }
    } else {
      // Pour les formes polygonales (carré, rectangle, polygone)
      const totalPoints = points.length - 1; // Exclure le point de fermeture
      const nombreAngles = Math.min(totalPoints, 4); // Maximum 4 angles pour carré/rectangle
      
      // D'abord placer les montants aux angles
      for (let i = 0; i < nombreAngles && i < nombreMontants; i++) {
        const anglePoint = points[i];
        montants.push({
          id: `montant-double-poutre-${i + 1}`,
          position: {
            x: anglePoint.x,
            y: hauteurStructurePrincipale,
            z: anglePoint.z
          },
          positionBas: {
            x: anglePoint.x,
            y: hauteurStructurePrincipale,
            z: anglePoint.z
          },
          positionHaut: {
            x: anglePoint.x,
            y: hauteurStructurePrincipale + hauteurDoublePoutre,
            z: anglePoint.z
          },
          hauteur: hauteurDoublePoutre,
          section: { largeur: 0.1, profondeur: 0.1 }
        });
      }
      
      // Distribuer les montants supplémentaires uniformément
      if (nombreMontants > nombreAngles) {
        const montantsSupplementaires = nombreMontants - nombreAngles;
        const montantsParCote = Math.floor(montantsSupplementaires / nombreAngles);
        
        for (let cote = 0; cote < nombreAngles; cote++) {
          const startPoint = points[cote];
          const endPoint = points[(cote + 1) % nombreAngles];
          
          for (let j = 1; j <= montantsParCote; j++) {
            const t = j / (montantsParCote + 1);
            const x = startPoint.x + (endPoint.x - startPoint.x) * t;
            const z = startPoint.z + (endPoint.z - startPoint.z) * t;
            
            montants.push({
              id: `montant-double-poutre-${montants.length + 1}`,
              position: {
                x: x,
                y: hauteurStructurePrincipale,
                z: z
              },
              positionBas: {
                x: x,
                y: hauteurStructurePrincipale,
                z: z
              },
              positionHaut: {
                x: x,
                y: hauteurStructurePrincipale + hauteurDoublePoutre,
                z: z
              },
              hauteur: hauteurDoublePoutre,
              section: { largeur: 0.1, profondeur: 0.1 }
            });
          }
        }
      }
    }
    
    return montants;
  }

  /**
   * Génère les poutres de la double poutre localement
   */
  private genererPoutresLocales(
    formeBase: GeometrieForme,
    hauteurStructurePrincipale: number,
    hauteurDoublePoutre: number,
    epaisseur: number
  ): { poutreBasse: PoutreDoublePoutre; poutreHaute: PoutreDoublePoutre } {
    // Générer le chemin des poutres suivant la forme
    const cheminPoutre = formeBase.points.map(point => ({
      x: point.x,
      y: 0, // Sera ajusté pour chaque poutre
      z: point.z
    }));
    
    const poutreBasse: PoutreDoublePoutre = {
      id: 'poutre-double-basse',
      chemin: cheminPoutre.map(point => ({
        ...point,
        y: hauteurStructurePrincipale
      })),
      hauteur: hauteurStructurePrincipale,
      epaisseur,
      type: 'poutre-double'
    };
    
    const poutreHaute: PoutreDoublePoutre = {
      id: 'poutre-double-haute',
      chemin: cheminPoutre.map(point => ({
        ...point,
        y: hauteurStructurePrincipale + hauteurDoublePoutre
      })),
      hauteur: hauteurStructurePrincipale + hauteurDoublePoutre,
      epaisseur,
      type: 'poutre-double'
    };
    
    return { poutreBasse, poutreHaute };
  }

  /**
   * Génération des poutres haute et basse de la double poutre
   */
  private genererPoutres3D(poutres: { poutreBasse: PoutreDoublePoutre; poutreHaute: PoutreDoublePoutre }, materiau: TypeMateriauGlobal, epaisseur: number, couleur: string): void {
    try {
      this.genererPoutre3D(poutres.poutreBasse, 'poutre-double-basse', materiau, epaisseur, couleur);
      this.genererPoutre3D(poutres.poutreHaute, 'poutre-double-haute', materiau, epaisseur, couleur);
    } catch (error) {
      // Erreur génération poutres
    }
  }

  /**
   * Génération d'une poutre 3D individuelle
   */
  private genererPoutre3D(poutre: PoutreDoublePoutre, nom: string, materiau: TypeMateriauGlobal, epaisseur: number, couleur: string): void {
    try {
      if (!poutre.chemin || poutre.chemin.length < 2) {
        // Chemin invalide pour la poutre
        return;
      }

      let geometrie: THREE.BufferGeometry;
      
      if (materiau === TypeMateriauGlobal.FER_A_BETON) {
        // Section circulaire - génération de segments cylindriques pour respecter la forme exacte
        const geometries: THREE.BufferGeometry[] = [];
        
        for (let i = 0; i < poutre.chemin.length - 1; i++) {
          const start = poutre.chemin[i];
          const end = poutre.chemin[i + 1];
          
          const distance = Math.sqrt(
            Math.pow(end.x - start.x, 2) +
            Math.pow(end.y - start.y, 2) +
            Math.pow(end.z - start.z, 2)
          );
          
          const segmentGeometry = new THREE.CylinderGeometry(
            epaisseur / 2,
            epaisseur / 2,
            distance,
            8
          );
          
          // Position et rotation du segment
          const midPoint = new THREE.Vector3(
            (start.x + end.x) / 2,
            (start.y + end.y) / 2,
            (start.z + end.z) / 2
          );
          
          const direction = new THREE.Vector3().subVectors(
            new THREE.Vector3(end.x, end.y, end.z),
            new THREE.Vector3(start.x, start.y, start.z)
          ).normalize();
          
          // Rotation pour aligner avec la direction
          const matrix = new THREE.Matrix4();
          const up = new THREE.Vector3(0, 1, 0); // Y est l'axe par défaut de CylinderGeometry
          const quaternion = new THREE.Quaternion().setFromUnitVectors(up, direction);
          matrix.makeRotationFromQuaternion(quaternion);
          matrix.setPosition(midPoint);
          
          segmentGeometry.applyMatrix4(matrix);
          geometries.push(segmentGeometry);
        }
        
        if (geometries.length > 0) {
          geometrie = BufferGeometryUtils.mergeGeometries(geometries);
        } else {
          // Aucune géométrie générée
          return;
        }
      } else {
        // Section carrée - génération de segments
        const geometries: THREE.BufferGeometry[] = [];
        
        for (let i = 0; i < poutre.chemin.length - 1; i++) {
          const start = poutre.chemin[i];
          const end = poutre.chemin[i + 1];
          
          const distance = Math.sqrt(
            Math.pow(end.x - start.x, 2) +
            Math.pow(end.y - start.y, 2) +
            Math.pow(end.z - start.z, 2)
          );
          
          const segmentGeometry = new THREE.BoxGeometry(epaisseur, epaisseur, distance);
          
          // Position et rotation du segment
          const midPoint = new THREE.Vector3(
            (start.x + end.x) / 2,
            (start.y + end.y) / 2,
            (start.z + end.z) / 2
          );
          
          const direction = new THREE.Vector3().subVectors(
            new THREE.Vector3(end.x, end.y, end.z),
            new THREE.Vector3(start.x, start.y, start.z)
          ).normalize();
          
          // Rotation pour aligner avec la direction
          const matrix = new THREE.Matrix4();
          const up = new THREE.Vector3(0, 1, 0); // Y est l'axe par défaut de BoxGeometry (hauteur)
          const quaternion = new THREE.Quaternion().setFromUnitVectors(up, direction);
          matrix.makeRotationFromQuaternion(quaternion);
          matrix.setPosition(midPoint);
          
          segmentGeometry.applyMatrix4(matrix);
          geometries.push(segmentGeometry);
        }
        
        if (geometries.length > 0) {
           geometrie = BufferGeometryUtils.mergeGeometries(geometries);
         } else {
           // Aucune géométrie générée
           return;
         }
      }

      // Matériau avec couleur des paramètres
      const couleurNumerique = parseInt(couleur.replace('#', ''), 16);
      const material = new THREE.MeshLambertMaterial({ color: couleurNumerique });
      
      // Mesh
      const mesh = new THREE.Mesh(geometrie, material);
      mesh.name = nom;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      
      // Stocker l'épaisseur originale pour les mises à jour
      mesh.userData.epaisseurOriginale = epaisseur;
      
      this.groupeDoublePoutre.add(mesh);

    } catch (error) {
      // Erreur génération poutre
    }
  }

  /**
   * Génération des montants verticaux de la double poutre
   */
  private genererMontants3D(montants: MontantDoublePoutre[], materiau: TypeMateriauGlobal, epaisseur: number, couleur: string): void {
    if (!montants || montants.length === 0) {
      // Aucun montant à générer
      return;
    }

    try {
      montants.forEach((montant, index) => {
        this.genererMontant3D(montant, `montant-double-${index}`, materiau, epaisseur, couleur);
      });
    } catch (error) {
      // Erreur génération montants
    }
  }

  /**
   * Génération d'un montant 3D individuel
   */
  private genererMontant3D(montant: MontantDoublePoutre, nom: string, materiau: TypeMateriauGlobal, epaisseur: number, couleur: string): void {
    try {
      // Création de la géométrie selon le matériau
      let geometrie: THREE.BufferGeometry;
      
      if (materiau === TypeMateriauGlobal.FER_A_BETON) {
        // Section circulaire
        geometrie = new THREE.CylinderGeometry(
          epaisseur / 2,
          epaisseur / 2,
          montant.hauteur,
          8
        );
      } else {
        // Section carrée
        geometrie = new THREE.BoxGeometry(
          epaisseur,
          montant.hauteur,
          epaisseur
        );
      }

      // Matériau avec couleur des paramètres
      const couleurNumerique = parseInt(couleur.replace('#', ''), 16);
      const material = new THREE.MeshLambertMaterial({ color: couleurNumerique });
      
      // Mesh
      const mesh = new THREE.Mesh(geometrie, material);
      mesh.name = nom;
      mesh.position.set(
        montant.position.x,
        montant.position.y + montant.hauteur / 2,
        montant.position.z
      );
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      
      // Stocker l'épaisseur originale pour les mises à jour
      mesh.userData.epaisseurOriginale = epaisseur;
      
      this.groupeDoublePoutre.add(mesh);
    } catch (error) {
      // Erreur génération montant
    }
  }



  /**
   * Nettoyage de la structure existante
   */
  private clearStructure(): void {
    while (this.groupeDoublePoutre.children.length > 0) {
      const child = this.groupeDoublePoutre.children[0];
      this.groupeDoublePoutre.remove(child);
      
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(mat => mat.dispose());
        } else {
          child.material.dispose();
        }
      }
    }
  }

  /**
   * Mise à jour de la couleur de la structure
   */
  updateCouleur(couleur: string): void {
    const couleurNumerique = parseInt(couleur.replace('#', ''), 16);
    
    this.groupeDoublePoutre.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (Array.isArray(child.material)) {
          child.material.forEach(mat => {
            if (mat instanceof THREE.MeshLambertMaterial) {
              mat.color.setHex(couleurNumerique);
            }
          });
        } else if (child.material instanceof THREE.MeshLambertMaterial) {
          child.material.color.setHex(couleurNumerique);
        }
      }
    });
  }

  /**
   * Alias pour updateCouleur pour compatibilité
   */
  mettreAJourCouleur(couleur: string): void {
    this.updateCouleur(couleur);
  }

  /**
   * Mise à jour de l'épaisseur de la structure
   */
  updateEpaisseur(nouvelleEpaisseur: number): void {
    if (!this.structureGeneree) return;

    this.groupeDoublePoutre.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        const nom = child.name;
        
        // Mise à jour des poutres
        if (nom.includes('poutre-double')) {
          const scale = nouvelleEpaisseur / (child.userData.epaisseurOriginale || 1);
          child.scale.set(scale, 1, scale);
          child.userData.epaisseurOriginale = nouvelleEpaisseur;
        }
        
        // Mise à jour des montants
        if (nom.includes('montant-double')) {
          const scale = nouvelleEpaisseur / (child.userData.epaisseurOriginale || 1);
          child.scale.set(scale, 1, scale);
          child.userData.epaisseurOriginale = nouvelleEpaisseur;
        }
      }
    });
  }

  /**
   * Mise à jour du matériau de la structure
   */
  updateMateriau(nouveauMateriau: TypeMateriauGlobal): void {
    if (!this.structureGeneree) return;

    // Pour un changement de matériau, il faut regénérer la géométrie
    // car la forme change (cylindre vs box)
    // On stocke les paramètres actuels et on regénère
    const parametresActuels = {
      active: true,
      hauteur: this.structureGeneree.poutres.poutreHaute.chemin[0]?.z || 0.3,
      nombreMontants: this.structureGeneree.montants.length,
      materiau: nouveauMateriau,
      epaisseur: 0.05, // Valeur par défaut, sera mise à jour
      couleur: '#8B4513' // Valeur par défaut, sera mise à jour
    };

    // Regénération nécessaire pour changer la forme géométrique
    // Cette méthode sera appelée depuis le module principal
  }

  /**
   * Destruction du service
   */
  dispose(): void {
    this.clearStructure();
  }
}